import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { processRemittances, RemittanceSummary } from '../../api/automationApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the Excel file containing the supplier data.',
  'Every sheet is scanned for rows whose payment status (column L) is "paid".',
  'Click Process Remittances. A ZIP with one workbook per contact downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function RemittancesPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<RemittanceSummary>>();

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload the supplier Excel file first.');
      return;
    }
    await run(() => processRemittances(files[0]), 'Processing failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <FileDrop
        label="Supplier remittance file"
        note="Column A: contact, B: date, D: invoice no, K: amount, L: payment status."
        required
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Processing…' : 'Process Remittances'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Processing…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ {result.filename} downloaded.</p>
          {result.summary && (
            <p className="mt-1 text-xs text-green-800">
              {result.summary.contacts} contact(s) with paid invoices.
            </p>
          )}
        </SuccessNotice>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload an Excel file above to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default RemittancesPanel;
